import CardHeader from "components/CardHeader";
import FilterToggleButton from "components/FilterToggleButton";
import PageMeta from "components/PageMeta";
import usePersistentFilterSet from "common/usePersistentFilterSet";
import ProjectTimeline, {
    getProjectLifecycleStatus,
    ProjectLifecycleStatus,
} from "components/ProjectTimeline";
import { useAdminConsoleVisibility } from "components/AdminConsole/adminConsoleVisibility";
import { Account } from "models/Account";
import { Project } from "models/Project";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, Col, Row } from "reactstrap";
import * as api from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const ProjectStatuses: ProjectLifecycleStatus[] = ["upcoming", "active", "completed"];

const Projects: React.FC = () => {
    const { intlLocale, t } = useOfficeTranslation();
    const date = (value: string) =>
        new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(new Date(value));
    const [projects, setProjects] = useState<Project[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const adminConsoleOpen = useAdminConsoleVisibility();
    const [visibleStatuses, toggleStatus] = usePersistentFilterSet(
        "galactic-filters-projects-statuses",
        ProjectStatuses,
    );

    useEffect(() => {
        Promise.all([api.getProjects(), api.getAccounts()])
            .then(([projectValues, accountValues]) => {
                setProjects(projectValues);
                setAccounts(accountValues);
            })
            .finally(() => setLoading(false));
    }, []);

    const accountNames = useMemo(
        () => new Map(accounts.map(account => [account.id, account.legal_name])),
        [accounts],
    );
    const filteredProjects = useMemo(
        () => projects
            .filter(project => project.name.toLowerCase().includes(query.trim().toLowerCase()))
            .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()),
        [projects, query],
    );
    const statusCounts = useMemo(
        () => filteredProjects.reduce<Record<ProjectLifecycleStatus, number>>(
            (counts, project) => {
                counts[getProjectLifecycleStatus(project)] += 1;
                return counts;
            },
            { upcoming: 0, active: 0, completed: 0 },
        ),
        [filteredProjects],
    );
    const visible = useMemo(
        () => filteredProjects.filter(project =>
            visibleStatuses.has(getProjectLifecycleStatus(project)),
        ),
        [filteredProjects, visibleStatuses],
    );

    return (
        <div className="page-content">
            <PageMeta title={t("common.projects")} />

            <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                    <h4 className="mb-1">{t("common.projects")}</h4>
                    <p className="text-muted mb-0">{t("projects.projects.projectsWorkersAndContractStatus")}</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <Link className="btn btn-light" to="/projects/payments">
                        <i className="mdi mdi-bank-transfer-out me-1" />
                        {t("projects.projects.weeklyPayments")}
                    </Link>
                    {adminConsoleOpen &&
                        <Link className="btn btn-light" to="/contract-template">
                            <i className="mdi mdi-file-document-edit-outline me-1" />
                            {t("projects.projects.contractTemplate")}
                        </Link>
                    }
                    <Link className="btn btn-primary" to="/projects/add">
                        <i className="mdi mdi-plus me-1" />
                        {t("common.newProject")}
                    </Link>
                </div>
            </div>

            <Card>
                <CardBody>
                    <CardHeader
                        title={t("projects.projects.projectOverview")}
                        icon="mdi-briefcase-outline"
                        count={visible.length}
                    />

                    <Row className="g-3 py-3 border-bottom">
                        <Col md={5}>
                            <div className="position-relative">
                                <input
                                    aria-label={t("projects.projects.searchProjects")}
                                    className="form-control ps-5"
                                    placeholder={t("projects.projects.searchProjects.1334959")}
                                    value={query}
                                    onChange={event => setQuery(event.target.value)}
                                />
                                <i
                                    className="mdi mdi-magnify position-absolute text-muted font-size-20"
                                    style={{ left: 16, top: 7 }}
                                />
                            </div>
                        </Col>
                        <Col md={7}>
                            <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-2 h-100">
                                <FilterToggleButton
                                    active={visibleStatuses.has("upcoming")}
                                    count={statusCounts.upcoming}
                                    label={t("projects.projects.upcoming")}
                                    onToggle={() => toggleStatus("upcoming")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("active")}
                                    count={statusCounts.active}
                                    label={t("projects.projects.active")}
                                    onToggle={() => toggleStatus("active")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("completed")}
                                    count={statusCounts.completed}
                                    label={t("projects.projects.completed")}
                                    onToggle={() => toggleStatus("completed")}
                                />
                            </div>
                        </Col>
                    </Row>

                    {loading && <p className="text-muted text-center py-5 mb-0">{t("projects.projects.loadingProjects")}</p>}

                    {!loading && visible.map(project => {
                        const workerNames = project.workers
                            .slice(0, 2)
                            .map(worker => accountNames.get(worker.account_id) ?? t("common.unknownAccount"));
                        const status = getProjectLifecycleStatus(project);

                        return (
                            <Link
                                className="d-block text-body text-decoration-none border-bottom py-3"
                                key={project.id}
                                to={`/projects/${project.id}`}
                            >
                                <Row className="align-items-center g-3">
                                    <Col md={3}>
                                        <strong>{project.name}</strong>
                                        <small className="d-block text-muted mt-1">
                                            {t(status === "upcoming" ? "common.status.upcoming" : status === "active" ? "common.status.active" : "common.status.completed")}
                                        </small>
                                    </Col>
                                    <Col md={3}>
                                        <ProjectTimeline compact project={project} />
                                        <div className="d-flex justify-content-between text-muted mt-2">
                                            <small>{date(project.start_date)}</small>
                                            <small>{date(project.end_date)}</small>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <small className="d-block text-muted">{t("common.budget")}</small>
                                        <span>
                                            {new Intl.NumberFormat(intlLocale, {
                                                style: "currency",
                                                currency: project.currency,
                                            }).format(project.budget / 100)}
                                        </span>
                                    </Col>
                                    <Col md={3}>
                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                            <Badge color="primary" pill>{project.workers.length}</Badge>
                                            <span>{workerNames.join(", ") || t("projects.projects.noWorkers")}</span>
                                            {project.workers.length > 2 &&
                                                <span className="text-muted">+{project.workers.length - 2}</span>
                                            }
                                        </div>
                                    </Col>
                                    <Col className="text-end" md={1}>
                                        <i className="mdi mdi-chevron-right text-muted font-size-18" />
                                    </Col>
                                </Row>
                            </Link>
                        );
                    })}

                    {!loading && visible.length === 0 &&
                        <p className="text-muted text-center mt-3 mb-0">{t("projects.projects.noProjectsFound")}</p>
                    }
                </CardBody>
            </Card>
        </div>
    );
};

export default Projects;
